/**
 * Multi-Account Storage — IndexedDB (2026-07-26)
 * ==============================================
 *
 * WhatsApp/Telegram-style account switcher: hold up to
 * `MAX_ACCOUNTS` sessions on the same device and swap between them
 * without needing to re-type credentials each time.
 *
 * Partitioned in its OWN idb-keyval store (`mesp-accounts / active`)
 * so it never collides with the TanStack Query cache (`mesp-query-
 * cache / tanstack`) nor with LiveKit persistence.
 *
 * Data shape — one entry per stored user:
 *
 *   {
 *     user_id:       "6a25...",     // canonical
 *     email:         "student@…",
 *     full_name:     "…",
 *     avatar:        null | "https://…",
 *     role:          "Student",
 *     access_token:  "eyJ…",        // may be stale — refresh on switch
 *     refresh_token: "eyJ…",
 *     added_at:      "2026-07-26T…",
 *     last_active_at:"2026-07-26T…",
 *   }
 *
 * The active-account pointer is a separate key so we can rotate it
 * atomically without rewriting the whole list.
 *
 * Public API:
 *   listAccounts()                – Promise<Account[]>
 *   getAccount(userId)            – Promise<Account | null>
 *   upsertAccount(snapshot)       – Promise<Account>
 *   removeAccount(userId)         – Promise<void>
 *   setActiveUserId(userId|null)  – Promise<void>
 *   getActiveUserId()             – Promise<string|null>
 *   captureFromLoginResponse(data) – convenience: build a snapshot
 *                                   from `/api/auth/login` response.
 */
import { get, set, del, createStore } from 'idb-keyval';

const IDB_DATABASE = 'mesp-accounts';
const IDB_STORE    = 'active';
const LIST_KEY     = 'accounts_v1';
const ACTIVE_KEY   = 'active_user_id_v1';

// Product-owner cap — 5 concurrent stored accounts is enough for
// mixed household use (parent + up to 4 kids) without bloating IDB.
export const MAX_ACCOUNTS = 5;

const store = createStore(IDB_DATABASE, IDB_STORE);

const _safeGet = async (key) => {
  try { return await get(key, store); } catch (_e) { return undefined; }
};
const _safeSet = async (key, value) => {
  try { await set(key, value, store); } catch (_e) { /* ignore */ }
};
const _safeDel = async (key) => {
  try { await del(key, store); } catch (_e) { /* ignore */ }
};

const _nowIso = () => new Date().toISOString();

export async function listAccounts() {
  const list = await _safeGet(LIST_KEY);
  return Array.isArray(list) ? list : [];
}

export async function getAccount(userId) {
  if (!userId) return null;
  const list = await listAccounts();
  return list.find((a) => a.user_id === String(userId)) || null;
}

export async function getActiveUserId() {
  const v = await _safeGet(ACTIVE_KEY);
  return typeof v === 'string' && v ? v : null;
}

export async function setActiveUserId(userId) {
  if (!userId) {
    await _safeDel(ACTIVE_KEY);
    return;
  }
  await _safeSet(ACTIVE_KEY, String(userId));
}

/**
 * Insert or update an account snapshot. If the user is already in
 * the list, we merge on top (never drop non-null fields). If we're
 * at capacity and this is a NEW user, evict the least-recently-active
 * entry (never the currently active one).
 */
export async function upsertAccount(snapshot) {
  if (!snapshot || !snapshot.user_id) return null;
  const uid = String(snapshot.user_id);
  const now = _nowIso();
  const list = await listAccounts();
  const idx = list.findIndex((a) => a.user_id === uid);
  const merged = {
    user_id:        uid,
    email:          snapshot.email          ?? list[idx]?.email       ?? null,
    full_name:      snapshot.full_name      ?? list[idx]?.full_name   ?? null,
    avatar:         snapshot.avatar         ?? list[idx]?.avatar      ?? null,
    role:           snapshot.role           ?? list[idx]?.role        ?? null,
    access_token:   snapshot.access_token   ?? list[idx]?.access_token   ?? null,
    refresh_token:  snapshot.refresh_token  ?? list[idx]?.refresh_token  ?? null,
    added_at:       list[idx]?.added_at || now,
    last_active_at: now,
  };
  if (idx >= 0) {
    list[idx] = merged;
  } else {
    if (list.length >= MAX_ACCOUNTS) {
      // Evict the least-recently-active — never the active one.
      const activeId = await getActiveUserId();
      const evictable = list
        .filter((a) => a.user_id !== activeId)
        .sort((a, b) => (a.last_active_at || '').localeCompare(b.last_active_at || ''));
      if (evictable.length) {
        const drop = evictable[0].user_id;
        const dropIdx = list.findIndex((a) => a.user_id === drop);
        if (dropIdx >= 0) list.splice(dropIdx, 1);
      }
    }
    list.push(merged);
  }
  await _safeSet(LIST_KEY, list);
  return merged;
}

/**
 * Remove one account from the store. Silently no-ops for unknown ids.
 * If the removed account was the active one, clears the active pointer.
 */
export async function removeAccount(userId) {
  if (!userId) return;
  const uid = String(userId);
  const list = await listAccounts();
  const next = list.filter((a) => a.user_id !== uid);
  if (next.length === list.length) return;
  await _safeSet(LIST_KEY, next);
  const activeId = await getActiveUserId();
  if (activeId === uid) await setActiveUserId(null);
}

/**
 * Wipe the multi-account store entirely — used only from
 * "Sign out of all accounts" panels or during a full IDB flush.
 */
export async function clearAllAccounts() {
  await _safeDel(LIST_KEY);
  await _safeDel(ACTIVE_KEY);
}

/**
 * Build a snapshot from a /auth/login OR /auth/register response body
 * plus the /auth/me user document. Either payload may be missing some
 * fields (login typically returns the full user; register returns a
 * subset) — this helper is lenient.
 */
export function captureFromLoginResponse(loginResp, userDoc) {
  const src = { ...(loginResp || {}), ...(userDoc || {}) };
  const uid = src.user_id || src.id || src._id;
  if (!uid) return null;
  return {
    user_id:       String(uid),
    email:         src.email || null,
    full_name:     src.full_name || null,
    avatar:        src.avatar_url || src.avatar || null,
    role:          src.role || null,
    access_token:  loginResp?.access_token || null,
    refresh_token: loginResp?.refresh_token || null,
  };
}
